import { useId, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { Icon, type IconName } from '@luacentrix/icons';
import { Badge, Button, IconButton, Progress, type BadgeTone } from './primitives';
import { cn, mergeIds } from './utils';

export type FileDropperStatus =
  | 'empty'
  | 'selected'
  | 'validating'
  | 'uploading'
  | 'success'
  | 'failed'
  | 'unsupported'
  | 'too_large';

export interface FileDropperSummary {
  name: string;
  extension: string;
  sizeLabel: string;
  checksumLabel?: string | undefined;
}

export interface FileDropperLabels {
  prompt: string;
  hint: string;
  browse: string;
  replace: string;
  remove: string;
  progress: string;
  status: Record<FileDropperStatus, string>;
  tone: Record<FileDropperStatus, BadgeTone>;
}

export interface FileDropperProps {
  status: FileDropperStatus;
  labels: FileDropperLabels;
  file?: FileDropperSummary | null;
  progress?: number | null;
  detail?: ReactNode;
  accept?: string;
  disabled?: boolean;
  busy?: boolean;
  onFiles: (files: File[]) => void;
  onRemove: () => void;
  className?: string;
  id?: string;
  describedBy?: string;
}

const STATUS_GLYPHS: Record<FileDropperStatus, IconName> = {
  empty: 'upload',
  selected: 'file',
  validating: 'loader',
  uploading: 'upload',
  success: 'check',
  failed: 'alertCircle',
  unsupported: 'alertTriangle',
  too_large: 'alertTriangle',
};

const GLYPH_TONES: Record<FileDropperStatus, string> = {
  empty: 'idle',
  selected: 'active',
  validating: 'active',
  uploading: 'active',
  success: 'ok',
  failed: 'bad',
  unsupported: 'warn',
  too_large: 'warn',
};

const MODEIFIER_NAMES: Record<FileDropperStatus, string> = {
  empty: 'empty',
  selected: 'selected',
  validating: 'validating',
  uploading: 'uploading',
  success: 'success',
  failed: 'failed',
  unsupported: 'unsupported',
  too_large: 'too-large',
};

export function defaultFileDropperLabels(): FileDropperLabels {
  return {
    prompt: 'Drop your script here',
    hint: 'Lua or Luau source up to 2 MB. Stored privately, never published.',
    browse: 'Browse files',
    replace: 'Replace',
    remove: 'Remove',
    progress: 'Upload progress',
    status: {
      empty: 'No file',
      selected: 'Ready',
      validating: 'Checking',
      uploading: 'Uploading',
      success: 'Stored',
      failed: 'Failed',
      unsupported: 'Unsupported',
      too_large: 'Too large',
    },
    tone: {
      empty: 'neutral',
      selected: 'neutral',
      validating: 'info',
      uploading: 'info',
      success: 'success',
      failed: 'danger',
      unsupported: 'warning',
      too_large: 'warning',
    },
  };
}

export function dragCarriesFiles(event: DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (types === undefined || types === null) {
    return false;
  }
  return Array.from(types).includes('Files');
}

export function FileDropper({
  status,
  labels,
  file = null,
  progress = null,
  detail = null,
  accept,
  disabled = false,
  busy = false,
  onFiles,
  onRemove,
  className,
  id,
  describedBy,
}: FileDropperProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const generated = useId();
  const inputId = id ?? `lc-dropper-${generated}`;
  const hintId = `${inputId}-hint`;
  const detailId = `${inputId}-detail`;
  const blocked = disabled === true || busy === true;
  const selected = file !== null && file !== undefined;
  const percent =
    progress === null ? null : Math.max(0, Math.min(100, Math.round(progress * 100)));

  const openPicker = (): void => {
    if (blocked) {
      return;
    }
    inputRef.current?.click();
  };

  const emit = (list: FileList | null): void => {
    if (list === null) {
      return;
    }
    const files = Array.from(list);
    if (files.length > 0) {
      onFiles(files);
    }
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>): void => {
    if (blocked || !dragCarriesFiles(event)) {
      return;
    }
    event.preventDefault();
    depth.current += 1;
    setDragging(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    if (blocked || !dragCarriesFiles(event)) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    if (blocked) {
      return;
    }
    event.preventDefault();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) {
      setDragging(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (blocked) {
      return;
    }
    emit(event.dataTransfer.files);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    emit(event.currentTarget.files);
    event.currentTarget.value = '';
  };

  const describedByIds = mergeIds(describedBy, detail === null ? hintId : detailId);

  return (
    <div
      className={cn(
        'lc-dropper',
        `lc-dropper--${MODEIFIER_NAMES[status]}`,
        dragging ? 'lc-dropper--dragging' : undefined,
        selected ? 'lc-dropper--filled' : undefined,
        blocked ? 'lc-dropper--blocked' : undefined,
        className,
      )}
      data-dragging={dragging ? 'true' : 'false'}
      aria-busy={busy ? 'true' : undefined}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        className="lc-visually-hidden"
        accept={accept}
        disabled={blocked}
        aria-describedby={describedByIds}
        onChange={handleChange}
        tabIndex={-1}
      />

      {selected ? (
        <div className="lc-dropper__row">
          <span className={cn('lc-dropper__glyph', `lc-dropper__glyph--${GLYPH_TONES[status]}`)}>
            <Icon name={STATUS_GLYPHS[status]} size="md" />
          </span>
          <span className="lc-dropper__body">
            <span className="lc-dropper__name" title={file.name}>
              {file.name}
            </span>
            <span className="lc-dropper__facts">
              <span className="lc-dropper__fact lc-dropper__fact--ext">
                {file.extension.toUpperCase()}
              </span>
              <span aria-hidden="true" className="lc-dropper__dot" />
              <span className="lc-dropper__fact">{file.sizeLabel}</span>
              {file.checksumLabel === undefined ? null : (
                <>
                  <span aria-hidden="true" className="lc-dropper__dot" />
                  <span className="lc-dropper__fact lc-dropper__checksum">{file.checksumLabel}</span>
                </>
              )}
              {percent === null ? null : (
                <>
                  <span aria-hidden="true" className="lc-dropper__dot" />
                  <span className="lc-dropper__fact lc-dropper__percent">{percent}%</span>
                </>
              )}
            </span>
            {percent === null ? null : (
              <Progress value={percent} label={labels.progress} />
            )}
          </span>
          <span className="lc-dropper__side">
            <Badge tone={labels.tone[status]}>{labels.status[status]}</Badge>
            <span className="lc-dropper__actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={openPicker}
                disabled={blocked}
                aria-label={labels.replace}
              >
                {labels.replace}
              </Button>
              <IconButton
                name="x"
                label={labels.remove}
                size="sm"
                onClick={onRemove}
                disabled={disabled}
              />
            </span>
          </span>
        </div>
      ) : (
        <button
          type="button"
          className="lc-dropper__target"
          onClick={openPicker}
          disabled={blocked}
          aria-describedby={describedByIds}
        >
          <span className={cn('lc-dropper__glyph', `lc-dropper__glyph--${GLYPH_TONES[status]}`)}>
            <Icon name={STATUS_GLYPHS[status]} size="lg" />
          </span>
          <span className="lc-dropper__body">
            <span className="lc-dropper__name">{labels.prompt}</span>
            <span className="lc-dropper__hint" id={hintId}>
              {labels.hint}
            </span>
          </span>
          <span className="lc-dropper__browse" aria-hidden="true">
            {labels.browse}
          </span>
        </button>
      )}

      {detail === null ? null : (
        <p
          id={detailId}
          className={cn('lc-dropper__detail', `lc-dropper__detail--${GLYPH_TONES[status]}`)}
          role={labels.tone[status] === 'danger' || labels.tone[status] === 'warning' ? 'alert' : 'status'}
        >
          {detail}
        </p>
      )}
    </div>
  );
}
